import React from 'react';
import classes from '../FlowNodes.module.scss';
import Typography from 'new/elements/Typography';
import { Handle, Position, useStore } from '@xyflow/react';
import { ReactComponent as NexsetIcon } from 'new/components/icons/nexsetsOutline.svg';
import { ReactComponent as DestinationIcon } from 'new/components/icons/fi-rr-arrow-right-to-bracket.svg';
import { RESOURCE_TYPE_DATA_SET, RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import { Stack } from 'new/elements/Stack';
import { useSelector } from 'react-redux';
import { map, reduce } from 'lodash';
import classnames from 'classnames';
import { createSelector } from 'reselect';

interface ResourceCountNodeProps {
  data: {
    flow: any;
    item: any;
    resourceType: string;
    flow_triggers: any;
    parentId: string;
    expandedNodes: string[];
  };
}

const names = {
  [RESOURCE_TYPE_DATA_SET]: 'NEXSETS',
  [RESOURCE_TYPE_SINK]: 'DESTINATIONS',
};

const icons = {
  [RESOURCE_TYPE_DATA_SET]: NexsetIcon,
  [RESOURCE_TYPE_SINK]: DestinationIcon,
};

const flowNodesSelector = (state: any) => state.entities.flowNodes;
const branchesSelector = (state: any) => state.pipelines.branches;

const countSelector = (flow) =>
  createSelector(flowNodesSelector, branchesSelector, (flowNodes, branches) => {
    const branch = branches[flow.nodeKey];

    const branchItems = map(branch && branch.items, (nodeKey) => flowNodes[nodeKey]);
    return reduce(
      branchItems,
      (acc, thisItem, idx) => {
        if (thisItem.type === 'DATA_SET' && thisItem.dataSetType === 'derived') {
          acc.derivedDataSetsCount += 1;
        } else if (thisItem.type === 'DATA_SINK') {
          acc.sinksCount += 1;
        }
        return acc;
      },
      {
        derivedDataSetsCount: 0,
        sinksCount: 0,
      },
    );
  });

const zoomSelector = (s) => s.transform[2];
export const ResourceCountNode = (props: ResourceCountNodeProps) => {
  const { data } = props;
  const { resourceType, flow, flow_triggers = {}, item = {}, parentId, expandedNodes } = data;
  const layoutDirection = (data as any).layoutDirection || 'LR';
  const isTB = layoutDirection === 'TB';

  const count = useSelector(countSelector(flow));

  const zoom = useStore(zoomSelector);
  const scale = 1 / zoom;

  const Icon = icons[resourceType] || NexsetIcon;

  if (parentId && expandedNodes.includes(parentId)) return null;

  return (
    <div
      className={classes.nodesContainer}
      style={{ padding: '16px 8px 8px 8px', minWidth: '120px' }}
    >
      <div
        style={{
          transformOrigin: 'bottom left',
          transform: `scale(${scale >= 1 ? scale : 1})`,
        }}
        className={classes.nameContainer}
      >
        <Typography className={classes.resourceType} variant="overline">
          {names[resourceType]}
        </Typography>
      </div>
      <Stack direction="row" alignItems="center" gap="8px">
        <div className={classes.resourceCountIconContainer}>
          <Icon width="20px" height="20px" />
        </div>
        <Typography className={classes.resourceCountLabel}>
          {resourceType === RESOURCE_TYPE_SINK
            ? count.sinksCount
            : (flow.children?.length || 0) + count.derivedDataSetsCount}
        </Typography>
      </Stack>
      <Handle
        isConnectable={false}
        className={classes.handle_left}
        type="target"
        position={isTB ? Position.Top : Position.Left}
        id="left"
      />
      <Handle
        isConnectable={false}
        className={classes.handle}
        type="source"
        position={isTB ? Position.Bottom : Position.Right}
        id="right"
      />
      <Handle
        isConnectable={false}
        className={classnames(classes.handle, classes['handle_DEPENDENCY'])}
        type={'source'}
        position={Position.Bottom}
        id="bottom-dependency"
      />
    </div>
  );
};
